import React, { useState, useCallback, useRef, useEffect, useMemo } from "react";
import { Lock, Share2 } from "lucide-react";
import { getMediaUrl, BACKEND_URL } from "../../utils";
import useAlwaysPlayingVideo from "../../utils/useAlwaysPlayingVideo";
import EmergeBadge from "../common/EmergeBadge";
import ResponsiveImage from "../common/ResponsiveImage";
import DiceRecordStamp from "../common/DiceRecordStamp";
import SwipeHintPill from "../common/SwipeHintPill";
import HERO_FALLBACK_DATAURI from "../../assets/heroFallback";

// Module-level singleton: only ONE BLKCardFrame instance can host the
// media-toggle hint pill at a time. Without this every card with both
// video+image would render its own hint, spamming the screen. The first
// card to register claims it; if that card unmounts, the slot is released
// so a later card can claim it. The pill itself reads localStorage for
// per-session persistence (see SwipeHintPill).
let hintHostId = null;
const HINT_STORAGE_KEY = "media-toggle";

const getRarityPillClass = (rarity) => {
  const r = (rarity || "common").toUpperCase();
  if (r === "MYTHIC") return "rarity-pill rarity-pill--mythic";
  if (r === "LEGENDARY") return "rarity-pill rarity-pill--legendary";
  if (r === "EPIC") return "rarity-pill rarity-pill--epic";
  if (r === "RARE") return "rarity-pill rarity-pill--rare";
  return "rarity-pill rarity-pill--common";
};

const getRarityGradient = (rarity) => {
  const r = (rarity || "common").toUpperCase();
  if (r === "LEGENDARY") return "from-amber-900/40 via-amber-800/20 to-[#0a0a14]";
  if (r === "EPIC") return "from-orange-900/40 via-orange-800/20 to-[#0a0a14]";
  if (r === "RARE") return "from-orange-900/40 via-orange-800/20 to-[#0a0a14]";
  return "from-gray-800/40 via-gray-900/20 to-[#0a0a14]";
};

/** Build a backend-proxy URL for a CDN video (fallback if direct CORS fails) */
const getProxyVideoUrl = (rawUrl) => {
  if (!rawUrl) return "";
  const trimmed = rawUrl.trim();
  if (trimmed.includes("media.blkpacks.fun")) {
    const cdnPath = trimmed.split("media.blkpacks.fun")[1] || "";
    return `${BACKEND_URL}/api/cdn${cdnPath}`;
  }
  return "";
};

const getSpectrumClass = (rarity) => {
  const r = (rarity || "common").toUpperCase();
  if (r === "LEGENDARY") return "card-spectrum card-spectrum-legendary";
  if (r === "EPIC") return "card-spectrum card-spectrum-epic";
  if (r === "RARE") return "card-spectrum card-spectrum-rare";
  return "card-spectrum card-spectrum-common";
};

const BLKCardFrame = ({
  imageUrl,
  videoUrl,
  name,
  rarity,
  serial,
  series,
  badge,
  isAR,
  isHobby,
  onClick,
  onShare,
  className = "",
  aspectRatio = "3/4",
  disableSpectrum = false,
  isHot = false,
  verificationId,
  collectibleId,
  // Deep-link helpers — pass whichever you have. Share button builds the
  // best possible URL in this priority order:
  //   1. explicit shareUrl (highest precedence — caller knows best)
  //   2. dropId → /api/drops/og/:id (renders OG meta + redirects to /drops/:id)
  //   3. verificationId / collectibleId → /verify/:slug
  //   4. otherwise the share button is HIDDEN (no more "fallback to homepage"
  //      which was dropping users on /drops regardless of what they shared).
  // Fixed 2026-05-15 per user directive.
  dropId,
  shareUrl,
}) => {
  const processedImage = getMediaUrl(imageUrl);
  const processedVideo = getMediaUrl(videoUrl);
  const proxyFallback = getProxyVideoUrl(videoUrl);
  const hasVideo = processedVideo && processedVideo.length > 10;
  const rarityLabel = (rarity || "COMMON").toUpperCase();
  const [imgFailed, setImgFailed] = useState(false);
  // Per-card local toggle: when both video + image exist, the user can
  // flip the video layer off to see the standalone card art. Defaults
  // to false (video visible) so the existing rip-moment UX is unchanged.
  // Toggle pill only appears in the footer when both media are present.
  const [imageOnly, setImageOnly] = useState(false);
  const hintRef = useRef(null);
  // Claim the singleton hint slot on mount; release on unmount. Without
  // this every card with both media would render its own hint pill,
  // crowding the page. Only the first claimant shows the hint.
  const [isHintHost] = useState(() => {
    if (hintHostId !== null) return false;
    const id = Math.random().toString(36).slice(2);
    hintHostId = id;
    return id;
  });
  useEffect(() => {
    return () => {
      if (isHintHost && hintHostId === isHintHost) hintHostId = null;
    };
  }, [isHintHost]);
  const [vidFailed, setVidFailed] = useState(false);
  const [retried, setRetried] = useState(false);
  const [videoSrc, setVideoSrc] = useState(processedVideo);
  const [triedProxy, setTriedProxy] = useState(false);
  const cardRef = useRef(null);
  const videoRef = useRef(null);
  const alwaysPlayRef = useAlwaysPlayingVideo();
  // Combined ref — assigns to both the internal videoRef (for error/proxy
  // retry logic below) and the always-play hook (for scroll-resume).
  const setVideoEl = useCallback((el) => {
    videoRef.current = el;
    alwaysPlayRef(el);
  }, [alwaysPlayRef]);

  // Update videoSrc when processedVideo changes
  useEffect(() => { setVideoSrc(processedVideo); }, [processedVideo]);

  // Play video when it's ready
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const tryPlay = () => { video.muted = true; video.play().catch(() => {}); };
    tryPlay();
    const onLoaded = () => tryPlay();
    const onInteraction = () => { tryPlay(); };
    video.addEventListener("loadeddata", onLoaded);
    document.addEventListener("touchstart", onInteraction, { once: true, passive: true });
    document.addEventListener("click", onInteraction, { once: true });
    return () => {
      video.removeEventListener("loadeddata", onLoaded);
      document.removeEventListener("touchstart", onInteraction);
      document.removeEventListener("click", onInteraction);
    };
  }, [videoSrc, vidFailed]);

  const handleVideoError = useCallback(() => {
    // If direct CDN failed and we have a proxy fallback, try that
    if (!triedProxy && proxyFallback) {
      setTriedProxy(true);
      setVideoSrc(proxyFallback);
      return;
    }
    setVidFailed(true);
  }, [triedProxy, proxyFallback]);

  // ── Lazy load (perf) ──
  // On the drops grid 18+ cards mount at once. With <video preload="auto">
  // every card would eagerly download its full 3–5 MB MP4 → ~80 MB cold
  // load per visit, made worse by Cloudflare not caching R2 custom-domain
  // assets (cf-cache-status: DYNAMIC). We only attach video src + start
  // image fetching when the card is within 200px of the viewport.
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = cardRef.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); obs.disconnect(); } },
      { rootMargin: "200px 0px", threshold: 0.01 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const handleImgError = useCallback((e) => {
    if (!retried && processedImage) {
      setRetried(true);
      const sep = processedImage.includes("?") ? "&" : "?";
      e.target.src = `${processedImage}${sep}_r=${Date.now()}`;
    } else {
      setImgFailed(true);
    }
  }, [processedImage, retried]);

  // Resolve the best share URL for this card. Returns null when there's
  // no meaningful deep-link target — in that case the share button is
  // hidden so the user never ends up on the homepage by accident.
  const resolvedShareUrl = useMemo(() => {
    if (shareUrl) return shareUrl;
    if (dropId) {
      const origin = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
      return `${origin}/api/drops/og/${encodeURIComponent(dropId)}`;
    }
    const slug = verificationId || collectibleId;
    if (slug) return `https://www.blkpacks.fun/verify/${encodeURIComponent(slug)}`;
    return null;
  }, [shareUrl, dropId, verificationId, collectibleId]);

  const handleShare = useCallback(async (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (onShare) { onShare(e); return; }
    if (!resolvedShareUrl) return; // shouldn't happen — button is gated below
    const url = resolvedShareUrl;
    const title = name ? `${name} · BLK PACKS` : "BLK PACKS";
    const text = `${title}${isHobby ? " · HOBBY" : (rarity ? ` · ${String(rarity).toUpperCase()}` : "")}${serial ? ` · #${serial}` : ""}`;
    try {
      if (navigator.share) {
        await navigator.share({ title, text, url });
        return;
      }
    } catch { /* user cancelled — that's fine */ return; }
    try {
      await navigator.clipboard.writeText(url);
      try { e.currentTarget.setAttribute("title", "Link copied!"); } catch { /* ignore */ }
    } catch { /* clipboard blocked — silent */ }
  }, [onShare, resolvedShareUrl, name, rarity, serial, isHobby]);

  const spectrumCls = disableSpectrum ? "border border-orange-500/30" : `${isHobby ? "card-spectrum card-spectrum-common" : getSpectrumClass(rarity)}${isHot ? " card-spectrum-hot" : ""}`;

  return (
    <div
      ref={cardRef}
      className={`relative rounded-xl overflow-hidden transition-all cursor-pointer group ${spectrumCls} ${className}`}
      onClick={onClick}
      data-testid="blk-card-frame"
    >
      {/* BLK Header */}
      <div className="bg-[#0a0a14] border-b border-orange-500/30 px-2 py-1 flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <span className="text-white font-black text-[10px] tracking-[0.12em]" style={{ textShadow: "0 0 6px rgba(249,115,22,0.4)" }}>BLK</span>
          <span className="text-amber-400/50 text-[7px] font-bold tracking-wider">PACKS</span>
        </div>
        <div className="flex items-center gap-1">
          {(isHobby || (rarity || "").toUpperCase() === "HOBBY") ? (
            <span
              className="px-1.5 py-0.5 rounded-full text-[7px] font-black tracking-wider brand-gradient-bg text-black shadow-[0_0_8px_rgba(249,115,22,0.4)]"
              data-testid="card-hobby-pill"
              title="Hobby Drop — ripping this pack earns +5 BLK Hobby Points"
            >
              HOBBY
            </span>
          ) : (
            <>
              {isAR && (
                <span className="px-1.5 py-0.5 rounded-full text-[7px] font-bold bg-orange-500/90 text-white">AR</span>
              )}
              <span className={getRarityPillClass(rarity)} style={{ fontSize: '7px', padding: '2px 6px' }}>
                {rarityLabel}
              </span>
            </>
          )}
          {/* Share — only rendered when we have a meaningful deep-link to
              share. Hides automatically when no dropId / verificationId /
              collectibleId / shareUrl is available so users can't share
              a card that would deep-link to the homepage. */}
          {resolvedShareUrl && (
            <button
              type="button"
              onClick={handleShare}
              className="w-5 h-5 rounded-full bg-white/5 hover:bg-orange-500/80 active:scale-90 flex items-center justify-center transition-colors flex-shrink-0"
              aria-label={`Share ${name || 'card'}`}
              title="Share"
              data-testid={`card-share-btn-${dropId || collectibleId || verificationId || serial || 'card'}`}
            >
              <Share2 className="text-white/80" size={10} />
            </button>
          )}
        </div>
      </div>

      {/* Image/Video */}
      <div className="relative bg-[#0a0a14]" style={{ aspectRatio }}>
        {/* Backdrop strategy — when we HAVE a real card image, the frame's
            own solid dark background (`bg-[#0a0a14]`) is the safest
            backdrop; any letterbox gap reads as crisp black instead of
            the generic "B BLK PACKS" fallback bleeding through.
            (Earlier 2026-06-16 attempt rendered a blurred-scaled copy
            of the image behind itself — produced a spotlight halo on
            tall art and made the chrome look hazy. Reverted to a clean
            black bed.) Falls back to HERO_FALLBACK_DATAURI ONLY when
            no image is set at all, so empty drops still show branded. */}
        {(!processedImage || imgFailed) && (
          <img
            src={HERO_FALLBACK_DATAURI}
            alt=""
            aria-hidden="true"
            decoding="async"
            className="absolute inset-0 w-full h-full object-cover pointer-events-none z-0"
          />
        )}
        {/* Rarity tint overlay — blends over the branded backdrop */}
        {/* Rarity tint overlay — neutral on hobby cards (no rarity exposure). */}
        <div className={`absolute inset-0 bg-gradient-to-b ${isHobby ? "from-orange-500/10 to-transparent" : getRarityGradient(rarity)} z-0 mix-blend-overlay opacity-60`} />

        {hasVideo && !vidFailed && !imageOnly ? (
          <video
            ref={setVideoEl}
            src={inView ? videoSrc : undefined}
            className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-300 z-[2]"
            autoPlay loop muted playsInline
            webkit-playsinline="true"
            preload={inView ? "metadata" : "none"}
            onError={handleVideoError}
            onEnded={(e) => { try { e.currentTarget.currentTime = 0; e.currentTarget.play().catch(() => {}); } catch {} }}
            data-testid="blk-card-video"
          />
        ) : null}
        {/* Image — ALWAYS rendered when present (no more "hidden" when a
            video is also present). The video sits on top at z-[2]; the
            image lives underneath at z-[1] and acts as a poster. If the
            video fails silently on iOS (autoplay denied, CORS preflight
            stall, decode error that didn't fire onError) the image
            stays visible — no more blank cards on Swap Spot, Locker,
            Marketplace. Fixed 2026-05-15 after user reported every card
            on Swap Spot rendered as the branded fallback only. */}
        {!imgFailed && processedImage ? (
          <ResponsiveImage
            src={processedImage}
            alt={name || "Card"}
            loading="lazy"
            fetchPriority="auto"
            className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-300 z-[1]"
            onError={handleImgError}
            sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 280px"
          />
        ) : null}
        {/* If image failed AND video failed/missing, show the card name over
            the already-rendered branded backdrop. */}
        {imgFailed && !hasVideo && name && (
          <div className="absolute inset-0 flex items-end justify-center p-3 z-[2] pointer-events-none">
            <span className="text-white/80 text-[10px] font-bold text-center leading-tight uppercase tracking-wide max-w-[90%] truncate">{name}</span>
          </div>
        )}

        {badge && (
          <div className="absolute top-1.5 left-1.5 z-10">{badge}</div>
        )}

        {/* EMERGE ledger stamp — bottom-LEFT so it never collides with the
            model-viewer AR cube button (always pinned bottom-right). */}
        <div
          className="absolute bottom-1.5 left-1.5 z-10 drop-shadow-[0_1px_2px_rgba(0,0,0,0.9)]"
          onClick={(e) => e.stopPropagation()}
        >
          <EmergeBadge
            size="xs"
            showDot={false}
            verificationId={verificationId}
            dropId={dropId}
            cardName={name}
          />
        </div>
      </div>

      {/* BLK Footer */}
      <div className="bg-[#0a0a14] border-t border-orange-500/30 px-2 py-1 flex items-center justify-between gap-1">
        <div className="flex items-center gap-1 min-w-0">
          <Lock size={8} className="text-gray-500 flex-shrink-0" />
          <span className="text-gray-500 text-[8px] font-bold truncate">
            {serial ? `#${serial}` : "Serial: ???"}
          </span>
          {/* Dice record stamp — appears once card has 3+ battles */}
          {collectibleId && <DiceRecordStamp collectibleId={collectibleId} />}
        </div>
        <div className="flex items-center gap-1.5 min-w-0">
          {/* Media-type pill — renders on EVERY card that has any media
              attached (image, video, or both). Behaviour by media set:
                • image + video → toggles between ▦ IMAGE and ▶ VIDEO
                • image only    → static "▦ IMAGE" indicator (no toggle)
                • video only    → static "▶ VIDEO" indicator (no toggle)
                • neither       → button is hidden
              Previously only rendered when BOTH existed, so ~80% of
              cards (image-only listings, video-only promo cards) showed
              no media indicator at all. User flagged this as a
              site-wide regression — fix lives here in BLKCardFrame so
              every page that renders a card inherits it automatically. */}
          {(() => {
            const showImageState = processedImage && !imgFailed;
            const showVideoState = hasVideo;
            if (!showImageState && !showVideoState) return null;
            const canToggle = showImageState && showVideoState;
            const label = canToggle
              ? (imageOnly ? "▶ VIDEO" : "▦ IMAGE")
              : showImageState
                ? "▦ IMAGE"
                : "▶ VIDEO";
            return (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  if (!canToggle) return; // single-media: pill is a label
                  setImageOnly(v => !v);
                  hintRef.current?.dismiss();
                }}
                className="rarity-pill rarity-pill--common"
                style={{
                  fontSize: '7px',
                  padding: '2px 6px',
                  lineHeight: 1,
                  cursor: canToggle ? 'pointer' : 'default',
                  opacity: canToggle ? 1 : 0.75,
                }}
                title={
                  canToggle
                    ? (imageOnly ? "Show video moment" : "Show card art")
                    : showImageState
                      ? "Image card"
                      : "Video moment"
                }
                aria-label={
                  canToggle
                    ? (imageOnly ? "Show video moment" : "Show card art")
                    : showImageState
                      ? "Image card"
                      : "Video moment"
                }
                data-testid="blk-card-media-toggle"
              >
                {label}
              </button>
            );
          })()}
          <span className="text-gray-500 text-[7px] font-bold tracking-wider uppercase truncate max-w-[60%] text-right">
            {series || "Collection"}
          </span>
        </div>
      </div>
      {/* First-run hint — only one card on the page (the singleton host)
          renders this. Points users toward the new media toggle pill.
          Auto-dismisses on first toggle click, after 6s, or once written
          to localStorage by SwipeHintPill. Hidden via sm:hidden so it
          only shows on touch surfaces where the toggle is least obvious. */}
      {isHintHost && hasVideo && processedImage && !imgFailed && (
        <SwipeHintPill
          ref={hintRef}
          storageKey={HINT_STORAGE_KEY}
          hideAt="sm:hidden"
          label="Tap to flip"
          testId="blk-card-media-toggle-hint"
        />
      )}
    </div>
  );
};

export default BLKCardFrame;
